import React, { useState } from 'react';
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt';
import HomeIcon from '@material-ui/icons/Home';
import PersonIcon from '@material-ui/icons/Person';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import ReplayIcon from '@material-ui/icons/Replay';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import EditIcon from '@material-ui/icons/Edit';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import EventIcon from '@material-ui/icons/Event';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import LockIcon from '@material-ui/icons/Lock';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import {
  ChevronRightIcon,
  CommentIcon,
  CorrectIcon,
  LinkCopyIcon,
  TransactionHistoryIcon
} from '../../../../../ui/Icons';
import Select, { ValueType } from 'react-select';
import moment from 'moment';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { getBookingModeTag } from '../../../../../shared/services/helpers/stay-type.helper';
import { BookOfferResponseModel } from '../../../../../shared/ui-models/book.ui-model';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import {
  hasAccess,
  hasRole
} from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { getBookOffer, reservationSwitchRoom } from '../../../../shared/services/data.service';
import { SwitchRoomModal } from '../switch-room-modal/switch-room-modal.component';
import { RaiseTicketModal } from '../../../../../shared/components/group-ui/raise-ticket/raise-ticket-modal.component';
import {
  ReservationSourceChannelModal,
  SourceChannelDraft
} from './reservation-source-channel-modal.component';
import {
  SAHEL_AREAS,
  dropdownItemStatus,
  getReservationStatusColor
} from '../../reservation-edit.helper';
import { updateOneReservations } from '../../../../shared/services/data.service';
import Tooltip from '../../../../../ui/Tooltip';
import Popover from '../../../../../ui/Popover';
import {
  getReservationChannelImage,
  getReservationChannelLabel,
  getReservationSourceImage,
  getReservationSourceLabel,
  resolveAttributionChannels
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import {
  getAdTypeLabel,
  getMarketingChannelLabel
} from '../../../../../shared/interfaces/attribution.interface';

export interface ReservationHeaderProps {
  data: {
    reservation: ReservationDetailsUiModel;
    reservationStatus: string | undefined;
    bookofferLoading: boolean;
    canChangeDates: boolean;
  };
  eventHandlers: {
    onStatusChange: (newStatus: string) => void;
    onOpenAddCommentModal: () => void;
    onReactivateModalOpen: (bookoffer: BookOfferResponseModel) => void;
    onBookofferLoadingChange: (loading: boolean) => void;
    onRefresh: (id: string) => Promise<any>;
    onOpenSplitReservationModal: () => void;
    onOpenChangeDatesModal: () => void;
    onOpenResendEmailModal: () => void;
    onOpenCommentsModal: () => void;
    onOpenHistoryModal: () => void;
  };
}

interface ActionItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabledReason?: string;
}

interface ActionGroup {
  title: string;
  items: ActionItem[];
}

// Stable references: ui/Popover recomputes its position whenever these change identity.
const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' } as const;
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' } as const;

const iconButtonClassName =
  'relative flex items-center justify-center px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors shadow-sm cursor-pointer';

export const ReservationHeader = ({
  data: { reservation, reservationStatus, bookofferLoading, canChangeDates },
  eventHandlers: {
    onStatusChange,
    onOpenAddCommentModal,
    onReactivateModalOpen,
    onBookofferLoadingChange,
    onRefresh,
    onOpenSplitReservationModal,
    onOpenChangeDatesModal,
    onOpenResendEmailModal,
    onOpenCommentsModal,
    onOpenHistoryModal
  }
}: ReservationHeaderProps) => {
  const reservationStatusColourStyles = (stateValue: any) => ({
    singleValue: (styles: any) => ({ ...styles, color: 'inherit' }),
    control: (styles: any) => {
      const colorObj = getReservationStatusColor(stateValue);
      return { ...styles, ...colorObj };
    },
    option: (styles: any, { data }: any) => {
      const colorObj = getReservationStatusColor(data.value);
      return { ...styles, ...colorObj };
    }
  });

  const canReopenCanceledReservation = hasRole([
    UserRole.RESERVATION_MANAGER,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  const statusOptions =
    reservation.status === ReservationStatus.CANCELED &&
    canReopenCanceledReservation &&
    reservation.source !== ReservationSource.CHANNEL_MANAGER
      ? [
          ...dropdownItemStatus[reservation.status],
          { label: 'Check in', value: ReservationStatus.CHECKED_IN }
        ]
      : dropdownItemStatus[reservation.status];

  const bookingModeTag = getBookingModeTag(
    reservation.bookingMode,
    reservation.checkInDate,
    reservation.checkOutDate
  );

  const isChannelManager = reservation.source === ReservationSource.CHANNEL_MANAGER;
  const channelManagerLockReason = `Updates only on ${
    reservation.channel ? reservation.channel.toUpperCase() : 'OTA directly'
  }`;

  const needsSahelValidation =
    !!reservation.areaId &&
    SAHEL_AREAS?.includes(reservation.areaId) &&
    (reservation.status === ReservationStatus.CHECKED_IN ||
      reservation.status === ReservationStatus.PENDING_CHECK_IN);

  const [showSwitchRoomModal, setShowSwitchRoomModal] = useState(false);
  const [switchRoomLoading, setSwitchRoomLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [showSourceChannelModal, setShowSourceChannelModal] = useState(false);
  const [sourceChannelDraft, setSourceChannelDraft] = useState<SourceChannelDraft>({});
  const [sourceChannelSaving, setSourceChannelSaving] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [showRaiseTicket, setShowRaiseTicket] = useState(false);

  const canEditSourceChannel = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess
  ]);
  // Mirrors the permission admin-connect enforces on POST /reservations/:id/resend-email.
  const canResendGuestEmail = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess
  ]);

  const saveAttribution = async (changes: SourceChannelDraft) => {
    const {
      source,
      channel,
      salesPerson,
      typeOfStay,
      brokerId,
      marketingChannel,
      messagingChannel
    } = changes;

    setSourceChannelSaving(true);
    const response = await updateOneReservations({
      id: reservation.id,
      source,
      channel,
      salesPerson,
      typeOfStay,
      // Only the broker id travels; server.service.property derives `brokerName`
      // from it and ignores any name sent alongside, so the two can never
      // disagree. Moving the segment off BROKER clears both server-side -- an
      // omitted field cannot express an unset over JSON.
      brokerId,
      // Same rule as the create form: a marketing or messaging answer picked
      // while the source was CX must not survive a switch to a source that
      // could never have produced it.
      ...resolveAttributionChannels(source, { marketingChannel, messagingChannel })
    });
    setSourceChannelSaving(false);

    if (isErrorInfo(response)) {
      birdToast.error('Failed to update reservation attribution');
      return false;
    }

    await onRefresh(reservation.id);
    birdToast.success('Reservation attribution updated');
    return true;
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const handleSwitchRoom = (unitTypeId: number, roomId: string) => {
    setSwitchRoomLoading(true);
    reservationSwitchRoom(
      Number(reservation?.id || 0),
      Number(reservation?.unitTypeId || 0),
      unitTypeId,
      roomId
    ).then((response) => {
      setSwitchRoomLoading(false);
      if (!isErrorInfo(response)) {
        birdToast.success('Reservation room switched, and an email is sent to Property Manager(s)');
        onRefresh(String(reservation?.id)).then(() => setShowSwitchRoomModal(false));
      } else {
        if (response.status === 409) {
          birdToast.error(
            'Switch room operation failed, as destination room is not available at the same dates, please contact support for taking further actions'
          );
        } else {
          birdToast.error(response.message || 'Switch room operation failed, Please contact support.');
        }
      }
    });
  };

  const handleReactivate = () => {
    onBookofferLoadingChange(true);
    getBookOffer(
      reservation.unitTypeId || '',
      moment(reservation.checkInDate).format('DD/MM/YYYY'),
      moment(reservation.checkOutDate).format('DD/MM/YYYY')
    ).then((res) => {
      onBookofferLoadingChange(false);
      if (
        !isErrorInfo(res) &&
        (res as BookOfferResponseModel).availableRooms &&
        (res as BookOfferResponseModel).availableRooms.length > 0
      ) {
        onReactivateModalOpen(res as BookOfferResponseModel);
      } else {
        birdToast.error('No available rooms to reactivate reservation');
      }
    });
  };

  const handleToggleValidation = () => {
    updateOneReservations({
      id: reservation.id,
      isValidated: !reservation.isValidated
    }).then(() => onRefresh(reservation.id));
  };

  const openSourceChannelModal = () => {
    setSourceChannelDraft({
      source: reservation.source as ReservationSource | undefined,
      channel: reservation.channel,
      salesPerson: reservation.salesPerson,
      typeOfStay: reservation.typeOfStay,
      brokerId: reservation.brokerId,
      marketingChannel: reservation.marketingChannel,
      messagingChannel: reservation.messagingChannel
    });
    setShowSourceChannelModal(true);
  };

  // Every reservation-level action lives in this one menu. Each item keeps the
  // exact permission / state check the standalone button it replaced had.
  const actionGroups: ActionGroup[] = [
    {
      title: 'Stay',
      items: [
        {
          key: 'switch-room',
          label: reservation.roomName ? 'Switch room' : 'Assign room',
          icon: <SwapHorizIcon style={{ fontSize: 16 }} />,
          onClick: () => setShowSwitchRoomModal(true)
        },
        ...(canChangeDates
          ? [
              {
                key: 'change-dates',
                label: 'Change dates',
                icon: <EventIcon style={{ fontSize: 16 }} />,
                onClick: onOpenChangeDatesModal,
                disabledReason: isChannelManager
                  ? channelManagerLockReason
                  : !reservation.source
                  ? 'Source not set'
                  : undefined
              }
            ]
          : []),
        ...(canReopenCanceledReservation
          ? [
              {
                key: 'shorten',
                label: 'Shorten reservation',
                icon: <CallSplitIcon style={{ fontSize: 16 }} />,
                onClick: onOpenSplitReservationModal
              }
            ]
          : [])
      ]
    },
    {
      title: 'Guest',
      items: [
        ...(canResendGuestEmail
          ? [
              {
                key: 'resend-email',
                label: 'Resend guest email',
                icon: <MailOutlineIcon style={{ fontSize: 16 }} />,
                onClick: onOpenResendEmailModal
              }
            ]
          : []),
        ...(needsSahelValidation
          ? [
              {
                key: 'validation',
                label: reservation.isValidated ? 'Mark as not validated' : 'Mark as validated',
                icon: <VerifiedUserIcon style={{ fontSize: 16 }} />,
                onClick: handleToggleValidation
              }
            ]
          : [])
      ]
    },
    {
      title: 'Booking',
      items: [
        ...(canEditSourceChannel && (reservation.channel || reservation.source)
          ? [
              {
                key: 'attribution',
                label: 'Edit source & attribution',
                icon: <EditIcon style={{ fontSize: 16 }} />,
                onClick: openSourceChannelModal
              }
            ]
          : []),
        {
          key: 'reactivate',
          label: 'Reactivate',
          icon: <ReplayIcon style={{ fontSize: 16 }} />,
          onClick: handleReactivate,
          disabledReason:
            reservation.status !== ReservationStatus.EXPIRED ? 'Expired only' : undefined
        }
      ]
    },
    {
      title: 'Support',
      items: hasAccess([
        PERMISSIONS_LIST.ticketsFullAccess,
        PERMISSIONS_LIST.ticketsCreateAccess
      ])
        ? [
            {
              key: 'raise-ticket',
              label: 'Raise ticket',
              icon: <ConfirmationNumberIcon style={{ fontSize: 16 }} />,
              onClick: () => setShowRaiseTicket(true)
            }
          ]
        : []
    }
  ].filter((group) => group.items.length > 0);

  const closeActionsMenu = () => setActionsAnchorEl(null);

  const renderAttributionTooltip = () => (
    <span className="text-lg">
      {getReservationSourceLabel(reservation.source)} →{' '}
      {getReservationChannelLabel(reservation.channel)}
      <br />
      Sales person: {reservation.salesPerson || 'Unassigned'}
      {/*
        Only shown when there is one. A blank "Broker: —" on the
        overwhelming majority of bookings that came through no
        broker would be noise in a tooltip this small.
      */}
      {reservation.brokerName && (
        <>
          <br />
          Broker: {reservation.brokerName}
        </>
      )}
      {/*
        The marketing side of the same question. Kept in this
        tooltip rather than given its own surface: this is
        already where someone looks to ask where a reservation
        came from, and the two answers belong together.
      */}
      {(reservation.marketingChannel || reservation.utm_campaign) && (
        <>
          <br />
          Marketing: {getMarketingChannelLabel(reservation.marketingChannel)} (
          {getAdTypeLabel(reservation.adType)})
          {reservation.utm_campaign && (
            <>
              <br />
              Campaign: {reservation.utm_campaign}
            </>
          )}
        </>
      )}
    </span>
  );

  const renderStatusSelect = () => (
    <Select
      isDisabled={
        isChannelManager ||
        !hasAccess([
          PERMISSIONS_LIST.reservationsFullAccess,
          PERMISSIONS_LIST.reservationsModifyAccess
        ])
      }
      styles={reservationStatusColourStyles(reservationStatus)}
      options={statusOptions}
      defaultValue={statusOptions?.[0]}
      onChange={(value: ValueType<{ value: ReservationStatus; label: string }, false>) => {
        onOpenAddCommentModal();
        onStatusChange(value ? value.value : '');
      }}
      className="react-select-container"
      classNamePrefix="react-select"
    />
  );

  const Separator = () => <span className="text-gray-300">·</span>;

  return (
    <>
      <div className="flex flex-col gap-2 mb-4 px-4 pt-4 md:px-0 md:pt-0 pb-4 border-b border-gray-200">
        {/* Line 1: which reservation, and what state it is in */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h1 className="text-2xl font-bold text-gray-800 m-0 whitespace-nowrap font-gilroy-medium">
              <span className="text-gray-400 font-normal">#</span>
              {reservation.id}
            </h1>

            <Tooltip title={urlCopied ? 'Copied!' : 'Copy link'} placement="top">
              <button
                type="button"
                onClick={handleCopyUrl}
                className="flex items-center justify-center size-7 rounded-md text-gray-400 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="Copy page URL"
              >
                {urlCopied ? <CorrectIcon /> : <LinkCopyIcon />}
              </button>
            </Tooltip>

            {bookingModeTag && (
              <Tooltip title={bookingModeTag.tooltip}>
                <div
                  className={`flex items-center gap-1 border px-2 py-1 rounded-md text-xl font-semibold whitespace-nowrap cursor-default ${
                    bookingModeTag.isInstant
                      ? 'bg-primary-50 text-primary-700 border-primary-100'
                      : 'bg-gray-50 text-gray-700 border-gray-200'
                  }`}
                >
                  {bookingModeTag.isInstant && <OfflineBoltIcon style={{ fontSize: 13 }} />}
                  {bookingModeTag.label}
                </div>
              </Tooltip>
            )}

            {reservation?.relatedReservationId && (
              <Tooltip title="Open related reservation" placement="top">
                <a
                  href={`${process.env.PUBLIC_URL}/reservations/edit/${reservation.relatedReservationId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xl font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100"
                >
                  Linked #{reservation.relatedReservationId} ↗
                </a>
              </Tooltip>
            )}

            {needsSahelValidation && !reservation.isValidated && (
              <Tooltip title="Mark as validated from the Actions menu">
                <div className="bg-orange-50 text-orange-700 border border-orange-300 px-2 py-1 rounded-md text-xl font-semibold whitespace-nowrap cursor-default">
                  Not validated
                </div>
              </Tooltip>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {reservation.status === ReservationStatus.EXPIRED && (
              <Button
                type={ButtonType.danger}
                className="!rounded-lg shadow-sm"
                onClick={handleReactivate}
                loading={bookofferLoading}
                size={ButtonSize.small}
              >
                <div className="flex items-center gap-1.5">
                  <ReplayIcon style={{ fontSize: 16 }} />
                  <span className="font-semibold">Reactivate</span>
                </div>
              </Button>
            )}

            {isChannelManager ? (
              <Tooltip title={channelManagerLockReason} placement="top">
                <div className="flex items-center gap-1 w-52">
                  <LockIcon style={{ fontSize: 15 }} className="text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">{renderStatusSelect()}</div>
                </div>
              </Tooltip>
            ) : (
              <div className="w-52">{renderStatusSelect()}</div>
            )}

            <Tooltip title="Comments" placement="top">
              <button
                type="button"
                onClick={onOpenCommentsModal}
                className={iconButtonClassName}
                aria-label="Open comments"
              >
                {reservation.messages && reservation.messages.length > 0 && (
                  <span className="absolute -top-2 -right-2 inline-flex items-center justify-center size-5 px-1 rounded-full bg-primary-600 text-white text-xs font-bold leading-none">
                    {reservation.messages.length}
                  </span>
                )}
                <CommentIcon />
              </button>
            </Tooltip>

            <Tooltip title="History" placement="top">
              <button
                type="button"
                onClick={onOpenHistoryModal}
                className={iconButtonClassName}
                aria-label="Open history"
              >
                <TransactionHistoryIcon />
              </button>
            </Tooltip>

            <button
              type="button"
              onClick={(event) => setActionsAnchorEl(event.currentTarget)}
              className="flex items-center gap-0.5 pl-3 pr-1.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors text-sm font-medium shadow-sm whitespace-nowrap cursor-pointer"
              aria-haspopup="menu"
              aria-expanded={Boolean(actionsAnchorEl)}
            >
              Actions
              <ArrowDropDownIcon style={{ fontSize: 20 }} />
            </button>
          </div>
        </div>

        {/* Line 2: where the guest is staying */}
        <div className="flex items-center gap-2 flex-wrap text-gray-700">
          {reservation.propertyName && (
            <Tooltip title="Property">
              <a
                href={`${process.env.PUBLIC_URL}/properties/edit/${reservation.propertyId}`}
                className="flex items-center gap-1 cursor-pointer hover:text-primary-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                <HomeIcon style={{ fontSize: 14 }} className="text-neutral-400" />
                <span className="text-xl font-gilroy-medium">{reservation.propertyName}</span>
              </a>
            </Tooltip>
          )}
          {reservation.unitName && (
            <>
              <span className="text-gray-300">/</span>
              <Tooltip title="Unit">
                <a
                  href={`${process.env.PUBLIC_URL}/unit-types/edit/${reservation.unitTypeId}`}
                  className="cursor-pointer hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-xl font-gilroy-medium">{reservation.unitName}</span>
                </a>
              </Tooltip>
            </>
          )}
          <span className="text-gray-300">/</span>
          {reservation.roomName ? (
            <Tooltip title="Room">
              <span className="text-xl font-gilroy-medium">{reservation.roomName}</span>
            </Tooltip>
          ) : (
            <Tooltip title="Click to assign a room">
              <button
                type="button"
                className="bg-orange-50 text-orange-700 border border-orange-300 hover:bg-orange-100 px-2 py-0.5 rounded-md text-xl font-semibold whitespace-nowrap cursor-pointer"
                onClick={() => setShowSwitchRoomModal(true)}
              >
                No room assigned
              </button>
            </Tooltip>
          )}
          {reservation.area && (
            <>
              <Separator />
              <Tooltip title="Area">
                <span className="flex items-center gap-1">
                  <LocationOnIcon style={{ fontSize: 14 }} className="text-neutral-400" />
                  <span className="text-xl font-gilroy-medium">{reservation.area}</span>
                </span>
              </Tooltip>
            </>
          )}
          {reservation.guestIds && reservation.guestIds.length > 0 && (
            <>
              <Separator />
              <Tooltip title="Guests">
                <span className="flex items-center gap-1">
                  <PersonIcon style={{ fontSize: 14 }} className="text-neutral-400" />
                  <span className="text-xl font-gilroy-medium">
                    {reservation.guestIds.length} guests
                  </span>
                </span>
              </Tooltip>
            </>
          )}
        </div>

        {/* Line 3: where it came from and who touched it */}
        <div className="flex items-center gap-2 flex-wrap text-lg text-gray-500">
          {(reservation.channel || reservation.source) && (
            <>
              <Tooltip title={renderAttributionTooltip()} placement="top">
                <span className="flex items-center gap-1 cursor-default">
                  <img
                    className="size-5 object-contain"
                    src={getReservationSourceImage(reservation.source)}
                    alt={getReservationSourceLabel(reservation.source)}
                  />
                  <ChevronRightIcon className="size-3 text-gray-300" />
                  <img
                    className="size-5 object-contain"
                    src={getReservationChannelImage(reservation.channel)}
                    alt={getReservationChannelLabel(reservation.channel)}
                  />
                  <span className="ml-1">
                    {getReservationSourceLabel(reservation.source)} →{' '}
                    {getReservationChannelLabel(reservation.channel)}
                  </span>
                </span>
              </Tooltip>
              <Separator />
            </>
          )}
          <span>
            Created by {reservation.createdByUsername} on{' '}
            {moment(reservation.createdAt).format('DD/MM/YYYY HH:mm')}
          </span>
          {reservation.modifiedByUsername && (
            <>
              <Separator />
              <span>
                Last modified by {reservation.modifiedByUsername} on{' '}
                {moment(reservation.updatedAt).format('DD/MM/YYYY HH:mm')}
              </span>
            </>
          )}
        </div>
      </div>

      <Popover
        open={Boolean(actionsAnchorEl)}
        anchorEl={actionsAnchorEl}
        onClose={closeActionsMenu}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
        className="mt-1.5 w-72 p-1.5 shadow-lg"
      >
        <div role="menu" aria-label="Reservation actions">
          {actionGroups.map((group, groupIndex) => (
            <div
              key={group.title}
              className={groupIndex > 0 ? 'border-t border-gray-100 mt-1 pt-1' : ''}
            >
              <div className="px-2.5 pt-2 pb-1 text-xs font-bold uppercase tracking-wider text-gray-400">
                {group.title}
              </div>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  disabled={!!item.disabledReason}
                  onClick={() => {
                    closeActionsMenu();
                    item.onClick();
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left text-sm font-medium text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  <span className="flex text-gray-400">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.disabledReason && (
                    <span className="ml-auto text-xs text-gray-400">{item.disabledReason}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </Popover>

      <SwitchRoomModal
        config={{ showModal: showSwitchRoomModal }}
        data={{ reservation, switchRoomLoading }}
        eventHandlers={{
          closeHandler: () => setShowSwitchRoomModal(false),
          switchRoomHandler: handleSwitchRoom
        }}
      />

      <RaiseTicketModal
        show={showRaiseTicket}
        onClose={() => setShowRaiseTicket(false)}
        context={{
          reservationRef: reservation.id,
          unitRef: reservation.unitTypeId,
          contactRef: reservation.userId,
          customerEmail: reservation.guestsList?.[0]?.email,
          description: `Reservation ${reservation.id} · ${reservation.primaryGuestName} · ${reservation.propertyName} ${reservation.unitName}`
        }}
      />

      <ReservationSourceChannelModal
        data={{
          reservation,
          showModal: showSourceChannelModal,
          sourceChannelDraft,
          sourceChannelSaving,
          disableSourceChannelFields: isChannelManager
        }}
        eventHandlers={{
          onClose: () => setShowSourceChannelModal(false),
          onDraftChange: setSourceChannelDraft,
          onSave: async () => {
            const saved = await saveAttribution(sourceChannelDraft);
            if (saved) setShowSourceChannelModal(false);
          }
        }}
      />
    </>
  );
};
